/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    CollectionActivityApi,
    CollectionActivityStoreV2Request,
    Configuration,
    ConfigurationParameters,
} from 'client-code/watch-stickiness';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IWatchStickinessServiceRepository } from '../interfaces';
import { Log } from '../logging/Log';
import { TCollectionActivityStoreParams, TServicesParams } from '../types';
import { UniqueIdentifier } from '../utils';

export class WatchStickinessServiceRepositoryImplV2 implements IWatchStickinessServiceRepository {
    private static TAG: string = 'WatchStickinessServiceRepositoryImplV2';

    private mParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private getConfigurationParameters(commonHeaders): ConfigurationParameters {
        return {
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders,
            },
        };
    }

    private getCommonRequestParams(commonHeaders: CommonHeaders['headers']) {
        return {
            applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
            applicationVersion: commonHeaders.ApplicationVersion,
            bodyId: this.mParams.bodyId,
            applicationName: commonHeaders.ApplicationName,
            deviceType: commonHeaders.DeviceType,
            productName: commonHeaders.ProductName,
            acceptLanguage: commonHeaders['Accept-Language'],
            xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            whiny: this.mParams.whiny || 'false',
        };
    }

    private async createCollectionActivityStoreFromServiceV2(
        collectionActivityStoreParams: TCollectionActivityStoreParams,
    ) {
        try {
            const commonHeaders = new CommonHeaders('seriesScreenItem').headers;
            const contentPreviewConfigParameters = this.getConfigurationParameters(commonHeaders);
            const configuration = new Configuration(contentPreviewConfigParameters);
            const previewsApi = new CollectionActivityApi(configuration);
            const contentPreviewV2RequestParameters: CollectionActivityStoreV2Request = {
                ...collectionActivityStoreParams,
                contentType: commonHeaders['Content-Type'],
                originRequestId: UniqueIdentifier.generateOriginRequestId(),
                ...this.getCommonRequestParams(commonHeaders),
            };
            const previewsContentResponse = await previewsApi.collectionActivityStoreV2(
                contentPreviewV2RequestParameters,
            );

            return previewsContentResponse.data;
        } catch (error: any) {
            Log.e(
                WatchStickinessServiceRepositoryImplV2.TAG,
                'getContentPreviewFromServiceV2()',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async createCollectionActivityStoreV2(
        collectionActivityStoreParams: TCollectionActivityStoreParams,
    ) {
        try {
            return await this.createCollectionActivityStoreFromServiceV2(
                collectionActivityStoreParams,
            );
        } catch (error) {
            Log.e(WatchStickinessServiceRepositoryImplV2.TAG, 'error', error);
            throw error;
        }
    }
}
